import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, RotateCcw, MessageCircle, Sparkles, ShoppingBag } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { MaskLines } from "../components/Reveal";
import { GlowCard } from "../components/GlowCard";
import { QUESTIONS, RESULTS, score } from "../lib/quiz";
import { slugForService } from "../lib/treatments";
import { useProducts } from "../lib/content";

const QuizProduct = ({ slug }) => {
  const p = useProducts().find((x) => x.slug === slug);
  if (!p) {
    return (
      <Link to="/shop" data-testid="quiz-product-shop-link" className="mt-8 inline-flex items-center gap-2 text-[0.72rem] tracking-[0.2em] uppercase text-[#C9A66B] hover:text-[#F6F2EC] transition-colors duration-300">
        <ShoppingBag className="w-4 h-4" /> Scopri lo shop Gioia Skincare
      </Link>
    );
  }
  return (
    <Link to={`/shop#${p.slug}`} data-testid="quiz-product-card" className="group mt-8 flex items-center gap-5 border border-[#F6F2EC]/15 bg-[#F6F2EC]/[0.04] p-3 pr-5 max-w-xl transition-colors duration-300 hover:border-[#C9A66B]">
      <div className="relative w-20 h-24 shrink-0 overflow-hidden bg-[#ECE5DA]">
        <img src={p.img} alt={p.it.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
      </div>
      <div className="min-w-0">
        <p className="text-[0.68rem] tracking-[0.22em] uppercase text-[#C9A66B]">Da usare a casa · Gioia Skincare</p>
        <p className="mt-1 font-serif-d text-xl sm:text-2xl font-light text-[#F6F2EC] leading-tight" data-testid="quiz-product-name">{p.it.name}</p>
        <p className="mt-1 text-sm text-[#b8ad9f] font-light">{p.it.tagline}</p>
      </div>
      <ArrowUpRight className="ml-auto w-5 h-5 shrink-0 text-[#C9A66B] transition-transform duration-500 group-hover:rotate-45" />
    </Link>
  );
};

const ease = [0.22, 1, 0.36, 1];

const Question = ({ step, onPick, onBack }) => {
  const q = QUESTIONS[step];
  return (
    <motion.div key={step} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, ease }} data-testid={`quiz-question-${step}`}>
      <p className="font-stencil tracking-[0.25em] text-[#C9A66B]">0{step + 1} / 0{QUESTIONS.length}</p>
      <h2 className="mt-5 font-serif-d font-light text-4xl sm:text-6xl leading-[1.02] text-[#F6F2EC]">{q.q}</h2>
      <div className="mt-10 grid sm:grid-cols-2 gap-3">
        {q.a.map(([label, w], i) => (
          <motion.button
            key={label}
            type="button"
            data-testid={`quiz-option-${step}-${i}`}
            onClick={() => onPick(w)}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 + i * 0.05, ease }}
            whileHover={{ x: 6 }}
            className="group text-left flex items-center justify-between gap-4 border border-[#F6F2EC]/20 px-6 py-5 text-[#F6F2EC] transition-colors duration-300 hover:border-[#C9A66B] hover:bg-[#C9A66B]/10"
          >
            <span className="text-base sm:text-lg font-light">{label}</span>
            <ArrowUpRight className="w-4 h-4 text-[#C9A66B] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </motion.button>
        ))}
      </div>
      {step > 0 && (
        <button type="button" onClick={onBack} data-testid="quiz-back-btn" className="mt-8 inline-flex items-center gap-2 text-sm text-[#b8ad9f] hover:text-[#F6F2EC] transition-colors duration-300">
          <ArrowLeft className="w-4 h-4" /> Indietro
        </button>
      )}
    </motion.div>
  );
};

const Result = ({ keys, onRestart }) => {
  const navigate = useNavigate();
  const r = RESULTS[keys[0]];
  const alt = keys[1] ? RESULTS[keys[1]] : null;
  const share = `https://wa.me/?text=${encodeURIComponent(`Ho fatto il quiz di Gioia Centro Estetico: il mio trattamento ideale è "${r.title}" ✨ Scopri il tuo: ${window.location.origin}/quiz-pelle`)}`;
  return (
    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease }} className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center" data-testid="quiz-result">
      <div className="lg:col-span-5">
        <motion.div initial={{ clipPath: "inset(100% 0 0 0 round 999px 999px 0 0)" }} animate={{ clipPath: "inset(0% 0 0 0 round 999px 999px 0 0)" }} transition={{ duration: 1.3, ease: [0.76, 0, 0.24, 1] }}
          className="relative overflow-hidden aspect-[4/5] rounded-t-[999px]">
          <img src={r.img} alt={r.title} style={{ objectPosition: r.pos }} className="absolute inset-0 w-full h-full object-cover" />
        </motion.div>
      </div>
      <div className="lg:col-span-7">
        <p className="overline text-[#C9A66B] flex items-center gap-3"><Sparkles className="w-4 h-4" /> Il tuo trattamento ideale</p>
        <h2 className="mt-5 font-serif-d font-light text-5xl sm:text-7xl leading-[0.95] text-[#F6F2EC]" data-testid="quiz-result-title">{r.title.replace(/-/g, "\u2011")}</h2>
        <p className="mt-6 text-[#d9d0c4] font-light leading-relaxed max-w-xl">{r.text}</p>
        <p className="mt-4 font-serif-d italic text-xl text-[#e6d3ae] max-w-xl">{r.tip}</p>
        {slugForService(r.service) && (
          <Link to={`/trattamenti/${slugForService(r.service)}`} data-testid="quiz-result-page-link" className="mt-5 inline-flex items-center gap-2 text-[0.72rem] tracking-[0.2em] uppercase text-[#C9A66B] underline underline-offset-8 hover:text-[#F6F2EC] transition-colors duration-300">
            Scopri il trattamento <ArrowUpRight className="w-4 h-4" />
          </Link>
        )}
        {alt && <p className="mt-6 text-sm text-[#b8ad9f]" data-testid="quiz-result-alt">Potrebbe piacerti anche: <span className="text-[#F6F2EC]">{alt.title}</span></p>}
        <QuizProduct slug={r.product} />
        <div className="mt-10 flex flex-col sm:flex-row flex-wrap gap-4">
          <button type="button" data-testid="quiz-book-btn" onClick={() => navigate("/#booking", { state: { service: r.service } })}
            className="group inline-flex justify-center items-center gap-3 rounded-full bg-[#C9A66B] text-[#14110F] px-8 py-4 text-[0.75rem] tracking-[0.2em] uppercase font-medium transition-colors duration-500 hover:bg-[#F6F2EC]">
            Prenota questo trattamento <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
          </button>
          <GlowCard result={r} />
          <a href={share} target="_blank" rel="noreferrer" data-testid="quiz-share-btn" className="inline-flex justify-center items-center gap-2 rounded-full border border-[#F6F2EC]/30 text-[#F6F2EC] px-7 py-4 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:border-[#C9A66B] hover:text-[#C9A66B]">
            <MessageCircle className="w-4 h-4" /> Condividi
          </a>
          <button type="button" onClick={onRestart} data-testid="quiz-restart-btn" className="inline-flex justify-center items-center gap-2 px-4 py-4 text-[0.72rem] tracking-[0.2em] uppercase text-[#b8ad9f] hover:text-[#F6F2EC] transition-colors duration-300">
            <RotateCcw className="w-4 h-4" /> Rifai il quiz
          </button>
        </div>
        <p className="mt-8 text-xs text-[#9a8f83]">Il quiz offre un'indicazione orientativa: durante la prima valutazione Gioia definirà con te il percorso più adatto.</p>
      </div>
    </motion.div>
  );
};

export default function SkinQuiz() {
  const [answers, setAnswers] = useState([]);
  const [started, setStarted] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); document.title = "Quiz: il trattamento giusto per te · Gioia"; }, []);
  const step = answers.length;
  const done = step >= QUESTIONS.length;
  const progress = started ? (step / QUESTIONS.length) * 100 : 0;

  return (
    <PageShell>
      <div className="max-w-[1440px] mx-auto -mx-5 sm:mx-auto" data-testid="quiz-page">
        <div className="relative bg-[#14110F] grain overflow-hidden px-6 sm:px-12 lg:px-16 py-14 sm:py-20 min-h-[620px]">
          <div className="absolute -right-40 -top-40 w-[40vw] h-[40vw] rounded-full bg-[#C9A66B]/10 blur-[120px] pointer-events-none" />
          <div className="absolute top-0 left-0 h-[2px] bg-[#C9A66B] transition-[width] duration-700" style={{ width: `${progress}%` }} data-testid="quiz-progress" />
          <div className="relative">
            {!started && (
              <div className="max-w-3xl" data-testid="quiz-intro">
                <p className="overline text-[#C9A66B]">Quiz · 1 minuto</p>
                <h1 className="mt-6 font-serif-d font-light text-[3rem] sm:text-7xl leading-[0.95] text-[#F6F2EC]">
                  <MaskLines lines={["Scopri il trattamento"]} delay={0.1} />
                  <MaskLines lines={["giusto per la tua pelle."]} delay={0.25} lineClass="italic text-[#C9A66B]" />
                </h1>
                <p className="mt-8 text-[#d9d0c4] font-light leading-relaxed max-w-lg">Cinque domande, un consiglio personalizzato firmato Gioia e la possibilità di prenotare subito.</p>
                <button type="button" onClick={() => setStarted(true)} data-testid="quiz-start-btn"
                  className="mt-10 group inline-flex items-center gap-3 rounded-full bg-[#C9A66B] text-[#14110F] px-9 py-4 text-[0.75rem] tracking-[0.2em] uppercase font-medium transition-colors duration-500 hover:bg-[#F6F2EC]">
                  Inizia il quiz <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
                </button>
              </div>
            )}
            {started && !done && <Question step={step} onPick={(w) => setAnswers([...answers, w])} onBack={() => setAnswers(answers.slice(0, -1))} />}
            {started && done && <Result keys={score(answers)} onRestart={() => setAnswers([])} />}
          </div>
        </div>
      </div>
    </PageShell>
  );
}
